import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { inputCls, labelCls, Spinner } from "@/components/common/states";
import { apiPost, apiErrorMessage } from "@/lib/api";
import { getAttribution } from "@/lib/utm";
import { track, Events } from "@/lib/analytics";
import { trackSuccessfulLead, trackEvent } from "@/lib/tracking";
import { CheckCircle2 } from "lucide-react";
import {
  PUNE_LOCATIONS, CONFIG_OPTIONS, PROPERTY_TYPES, PURPOSE_OPTIONS, TIMELINE_OPTIONS,
  REQUIREMENT_TYPES, PREFERRED_REGION_OPTIONS,
} from "@/lib/constants";

const ENDPOINT = {
  requirement: "/requirements",
  contact: "/contact",
  "schedule-visit": "/schedule-visit",
  callback: "/leads",
  property: "/leads",
  project: "/leads",
  location: "/leads",
  brochure: "/leads",
  website: "/leads",
};

// variant controls which fields show: "full" | "quick" | "contact"
export default function LeadForm({
  source = "website",
  variant = "full",
  requirementMode = false,
  defaults = {},
  title,
  description,
  submitLabel = "Submit",
  successTitle,
  successMessage,
  onSuccess,
  redirectOnSuccess = true,
  compact = false,
}) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "", property_type: "",
    budget: "", configuration: "", purpose: "", timeline: "", message: "",
    requirement_type: "", preferred_region: "", preferred_location: "",
    company: "", ...defaults,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [started, setStarted] = useState(false);

  const gaBase = () => ({
    form_type: defaults.form_type || source,
    lead_source: source,
    project_name: defaults.project_name || defaults.property_name || null,
    project_slug: defaults.project_slug || defaults.property_slug || null,
    location: defaults.location || null,
    cta_source: defaults.cta_source || null,
    ...getAttribution(),
  });

  const set = (k) => (e) => {
    if (!started) {
      setStarted(true);
      track(Events.LEAD_STARTED, { source });
      trackEvent("lead_form_started", gaBase());
    }
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const showFull = variant === "full";
  const showContact = variant === "contact" || showFull;
  const isMinimal = variant === "minimal";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) return setError("Please enter your name.");
    if (form.phone.replace(/\D/g, "").length < 8) return setError("Please enter a valid phone number.");
    setLoading(true);
    try {
      const payload = {
        ...form,
        source,
        requirement: form.requirement || defaults.requirement || null,
        form_type: defaults.form_type || source,
        cta_source: defaults.cta_source || null,
        property_slug: defaults.property_slug || null,
        property_name: defaults.property_name || null,
        project_slug: defaults.project_slug || null,
        project_name: defaults.project_name || null,
        region: defaults.region || null,
        ...getAttribution(),
      };
      const res = await apiPost(ENDPOINT[source] || "/leads", payload);
      if (!res.success) throw new Error(res.error?.message || "Submission failed");
      track(Events.LEAD_SUBMITTED, { source });
      trackEvent("lead_form_submitted", gaBase());
      if (source === "brochure") trackEvent("brochure_request", gaBase());
      // Canonical SUCCESSFUL_LEAD — only after backend confirms persistence
      trackSuccessfulLead({
        leadId: res.data?.id,
        source,
        region: defaults.region || null,
        projectName: defaults.property_name || defaults.project_name || null,
        location: form.location || defaults.location || null,
      });
      if (onSuccess) onSuccess();
      if (redirectOnSuccess) {
        navigate("/thank-you", {
          state: {
            formType: defaults.form_type || source,
            projectName: defaults.project_name || defaults.property_name || null,
            officialUrl: defaults.official_url || null,
          },
        });
        return;
      }
      setDone(true);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-success" className="rounded-lg border border-border bg-white p-8 text-center">
        <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-4" aria-hidden />
        <h3 className="font-display text-2xl text-foreground mb-2">{successTitle || "Thank you"}</h3>
        <p className="text-muted-foreground">
          {successMessage || "Your enquiry has been received. Our property advisor will get in touch with you shortly."}
        </p>
      </div>
    );
  }

  return (
    <form data-testid={`lead-form-${source}`} onSubmit={submit} className="space-y-4">
      {title && <h3 className="font-display text-2xl text-foreground">{title}</h3>}
      {description && <p className="text-muted-foreground -mt-1">{description}</p>}

      {/* honeypot */}
      <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off"
        className="hidden" aria-hidden="true" />

      <div className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label className={labelCls} htmlFor={`nm-${source}`}>Name *</label>
          <Input id={`nm-${source}`} data-testid="lead-name" value={form.name} onChange={set("name")}
            placeholder="Your full name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor={`ph-${source}`}>Phone *</label>
          <Input id={`ph-${source}`} data-testid="lead-phone" value={form.phone} onChange={set("phone")}
            placeholder="10-digit mobile number" className={inputCls} inputMode="tel" />
        </div>
      </div>

      {showContact && (
        <div>
          <label className={labelCls} htmlFor={`em-${source}`}>Email</label>
          <Input id={`em-${source}`} data-testid="lead-email" value={form.email} onChange={set("email")}
            placeholder="you@example.com" className={inputCls} type="email" />
        </div>
      )}

      {requirementMode && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>What are you looking for?</label>
              <select data-testid="lead-requirement-type" value={form.requirement_type} onChange={set("requirement_type")} className={inputCls}>
                <option value="">Select</option>
                {REQUIREMENT_TYPES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Where are you looking?</label>
              <select data-testid="lead-region" value={form.preferred_region} onChange={set("preferred_region")} className={inputCls}>
                <option value="">Select region</option>
                {PREFERRED_REGION_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Specific locality (optional)</label>
            <Input data-testid="lead-preferred-location" value={form.preferred_location} onChange={set("preferred_location")}
              placeholder="e.g. Wakad, Hinjewadi, Baner, Kharadi" className={inputCls} list="ec-locality-list" autoComplete="off" />
            <datalist id="ec-locality-list">
              {PUNE_LOCATIONS.map((l) => <option key={l} value={l} />)}
            </datalist>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Property Type</label>
              <select data-testid="lead-type" value={form.property_type} onChange={set("property_type")} className={inputCls}>
                <option value="">Any</option>
                {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Configuration</label>
              <select data-testid="lead-config" value={form.configuration} onChange={set("configuration")} className={inputCls}>
                <option value="">Any</option>
                {CONFIG_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Budget</label>
            <Input data-testid="lead-budget" value={form.budget} onChange={set("budget")}
              placeholder="e.g. ₹80 L - ₹1.2 Cr" className={inputCls} />
          </div>
        </>
      )}

      {showFull && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Preferred Location</label>
              <select data-testid="lead-location" value={form.location} onChange={set("location")} className={inputCls}>
                <option value="">Select area</option>
                {PUNE_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Property Type</label>
              <select data-testid="lead-type" value={form.property_type} onChange={set("property_type")} className={inputCls}>
                <option value="">Any</option>
                {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Budget</label>
              <Input data-testid="lead-budget" value={form.budget} onChange={set("budget")}
                placeholder="e.g. ₹80 L - ₹1.2 Cr" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Configuration</label>
              <select data-testid="lead-config" value={form.configuration} onChange={set("configuration")} className={inputCls}>
                <option value="">Any</option>
                {CONFIG_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Purpose</label>
              <select data-testid="lead-purpose" value={form.purpose} onChange={set("purpose")} className={inputCls}>
                <option value="">Select</option>
                {PURPOSE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Timeline</label>
              <select data-testid="lead-timeline" value={form.timeline} onChange={set("timeline")} className={inputCls}>
                <option value="">Select</option>
                {TIMELINE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </>
      )}

      {!isMinimal && (
        <div>
          <label className={labelCls}>{requirementMode ? "Anything else we should know?" : "Message"}</label>
          <Textarea data-testid="lead-message" value={form.message} onChange={set("message")}
            placeholder={requirementMode ? "Tell us anything important about your requirement..." : "Tell us what you're looking for"} rows={compact ? 2 : 3}
            className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary" />
        </div>
      )}

      {error && <p data-testid="lead-error" className="text-sm text-destructive">{error}</p>}

      <Button data-testid="lead-submit" type="submit" disabled={loading}
        className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md text-sm font-semibold tracking-wide transition-colors">
        {loading ? <Spinner /> : submitLabel}
      </Button>
      <p className="text-xs text-muted-foreground text-center">
        By submitting, you agree to be contacted by Endeavour Castle regarding your enquiry.
      </p>
    </form>
  );
}
